import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CompaniesPage from './pages/CompaniesPage';
import CompanyDetailsPage from './pages/CompanyDetailsPage';
import ClassificationsPage from './pages/ClassificationsPage';
import MessagesPage from './pages/MessagesPage';
import TrainingsPage from './pages/TrainingsPage';
import NewsPage from './pages/NewsPage';
import AuthSystemPage from './pages/AuthSystemPage';
import PartnersPage from './pages/PartnersPage';
import ContractsPage from './pages/ContractsPage';
import TendersPage from './pages/TendersPage';
import EventsPage from './pages/EventsPage';
import FilesPage from './pages/FilesPage';
import OutwardLettersPage from './pages/OutwardLettersPage';
import FundSystemPage from './pages/FundSystemPage';
import CitiesPage from './pages/CitiesPage';
import AreasPage from './pages/AreasPage';
import AreasAndCitiesPage from './pages/AreasAndCitiesPage';
import SettingsPage from './pages/SettingsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import AIAgentPage from './pages/AIAgentPage';
import EntityDetailPage from './pages/EntityDetailPage';
import EntityEditPage from './pages/EntityEditPage';
import BoardMeetingsPage from './pages/BoardMeetingsPage';
import BoardAttachmentsPage from './pages/BoardAttachmentsPage';
import GalleryPage from './pages/GalleryPage';
import BranchesPage from './pages/BranchesPage';
import RelatedLinksPage from './pages/RelatedLinksPage';
import InfoPagesPage from './pages/InfoPagesPage';
import DocumentTemplatesPage from './pages/DocumentTemplatesPage';
import ActivityLogPage from './pages/ActivityLogPage';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

function AppContent() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // Handle redirects from 404 page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');
    if (redirectPath && redirectPath !== location.pathname) {
      // Remove the redirect parameter and navigate to the intended path
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, location.pathname]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/ai-agent" element={<ProtectedRoute><Layout><AIAgentPage /></Layout></ProtectedRoute>} />
      <Route path="/partners/new" element={<ProtectedRoute><Layout><EntityEditPage entity="partners" /></Layout></ProtectedRoute>} />
      <Route path="/partners/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="partners" /></Layout></ProtectedRoute>} />
      <Route path="/partners/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="partners" /></Layout></ProtectedRoute>} />
      <Route path="/tenders/new" element={<ProtectedRoute><Layout><EntityEditPage entity="tenders" /></Layout></ProtectedRoute>} />
      <Route path="/tenders/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="tenders" /></Layout></ProtectedRoute>} />
      <Route path="/tenders/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="tenders" /></Layout></ProtectedRoute>} />
      <Route path="/news/new" element={<ProtectedRoute><Layout><EntityEditPage entity="news" /></Layout></ProtectedRoute>} />
      <Route path="/news/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="news" /></Layout></ProtectedRoute>} />
      <Route path="/news/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="news" /></Layout></ProtectedRoute>} />
      <Route path="/trainings/new" element={<ProtectedRoute><Layout><EntityEditPage entity="trainings" /></Layout></ProtectedRoute>} />
      <Route path="/trainings/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="trainings" /></Layout></ProtectedRoute>} />
      <Route path="/trainings/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="trainings" /></Layout></ProtectedRoute>} />
      <Route path="/contracts/new" element={<ProtectedRoute><Layout><EntityEditPage entity="contracts" /></Layout></ProtectedRoute>} />
      <Route path="/contracts/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="contracts" /></Layout></ProtectedRoute>} />
      <Route path="/contracts/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="contracts" /></Layout></ProtectedRoute>} />
      <Route path="/fund-system/new" element={<ProtectedRoute><Layout><EntityEditPage entity="fund_system" /></Layout></ProtectedRoute>} />
      <Route path="/fund-system/:id" element={<ProtectedRoute><Layout><EntityDetailPage entity="fund_system" /></Layout></ProtectedRoute>} />
      <Route path="/fund-system/:id/edit" element={<ProtectedRoute><Layout><EntityEditPage entity="fund_system" /></Layout></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><Layout><CompaniesPage /></Layout></ProtectedRoute>} />
      <Route path="/companies/:id" element={<ProtectedRoute><Layout><CompanyDetailsPage /></Layout></ProtectedRoute>} />
      <Route path="/contractors" element={<Navigate to="/companies" replace />} />
      <Route path="/auth-system" element={<ProtectedRoute><Layout><AuthSystemPage /></Layout></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><Layout><PartnersPage /></Layout></ProtectedRoute>} />
      <Route path="/classifications" element={<ProtectedRoute><Layout><ClassificationsPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><Layout><ContractsPage /></Layout></ProtectedRoute>} />
      <Route path="/tenders" element={<ProtectedRoute><Layout><TendersPage /></Layout></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Layout><EventsPage /></Layout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Layout><MessagesPage /></Layout></ProtectedRoute>} />
      <Route path="/trainings" element={<ProtectedRoute><Layout><TrainingsPage /></Layout></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><Layout><NewsPage /></Layout></ProtectedRoute>} />
      <Route path="/board-meetings" element={<ProtectedRoute><Layout><BoardMeetingsPage /></Layout></ProtectedRoute>} />
      <Route path="/board-attachments" element={<ProtectedRoute><Layout><BoardAttachmentsPage /></Layout></ProtectedRoute>} />
      <Route path="/gallery" element={<ProtectedRoute><Layout><GalleryPage /></Layout></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute><Layout><BranchesPage /></Layout></ProtectedRoute>} />
      <Route path="/related-links" element={<ProtectedRoute><Layout><RelatedLinksPage /></Layout></ProtectedRoute>} />
      <Route path="/info-pages" element={<ProtectedRoute><Layout><InfoPagesPage /></Layout></ProtectedRoute>} />
      <Route path="/document-templates" element={<ProtectedRoute><Layout><DocumentTemplatesPage /></Layout></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><Layout><ActivityLogPage /></Layout></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><Layout><FilesPage /></Layout></ProtectedRoute>} />
      <Route path="/outward-letters" element={<ProtectedRoute><Layout><OutwardLettersPage /></Layout></ProtectedRoute>} />
      <Route path="/fund-system" element={<ProtectedRoute><Layout><FundSystemPage /></Layout></ProtectedRoute>} />
      <Route path="/cities" element={<ProtectedRoute><Layout><CitiesPage /></Layout></ProtectedRoute>} />
      <Route path="/areas" element={<ProtectedRoute><Layout><AreasPage /></Layout></ProtectedRoute>} />
      <Route path="/areas-cities" element={<ProtectedRoute><Layout><AreasAndCitiesPage /></Layout></ProtectedRoute>} />
      <Route path="/admin-users" element={<ProtectedRoute><Layout><AdminUsersPage /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/profile-settings" element={<ProtectedRoute><Layout><ProfileSettingsPage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
